// src/store/deals/deals.store.ts

import { useMemo } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import dealsService from "@/services/deals.service";
import { TDeal, TDealsFilters, TCreateDealPayload } from "@/models/deals.model";
import { getErrorFromBackend } from "@/utils/errors";
import { DealsByStatusState, DealsState } from "./deals.types";

const DEFAULT_PAGE_SIZE = 20;

const createEmptyStatusState = (pageSize: number): DealsByStatusState => ({
    ids: [],
    page: 0,
    pageSize,
    totalPages: 0,
    totalCount: 0,
    loading: false,
    initialized: false,
});

const initialDealsState: DealsState = {
    initialized: false,
    filters: {},
    entities: {},
    byPipeline: {},
};

/* ------------------------------------------------------------------ *
 * Immutable helpers — each returns the new pieces of state.
 * ------------------------------------------------------------------ */

/** Replace one column inside byPipeline, creating pipeline/column as needed. */
const withColumn = (
    state: DealsState,
    pipelineId: number,
    statusId: number,
    mapColumn: (column: DealsByStatusState) => DealsByStatusState,
    createIfMissing = false
): DealsState["byPipeline"] | undefined => {
    const pipeline = state.byPipeline[pipelineId];
    const column = pipeline?.byStatus[statusId];
    if (!column && !createIfMissing) return undefined;

    const baseColumn = column ?? createEmptyStatusState(DEFAULT_PAGE_SIZE);
    return {
        ...state.byPipeline,
        [pipelineId]: {
            byStatus: {
                ...(pipeline?.byStatus ?? {}),
                [statusId]: mapColumn(baseColumn),
            },
        },
    };
};

const sortIdsByCreatedDesc = (ids: number[], entities: Record<number, TDeal>): number[] =>
    [...ids].sort((a, b) => {
        const dealA = entities[a];
        const dealB = entities[b];
        if (!dealA || !dealB) return 0;
        return new Date(dealB.created).getTime() - new Date(dealA.created).getTime();
    });

/**
 * Moves a deal between columns (optimistic update and its server confirmation).
 * Mirrors the old `dealUpdated` reducer: the entity is updated FIRST — the
 * column bookkeeping below bails out when the board hasn't loaded the affected
 * columns (e.g. the deal reached the store via GET /deal/:id on the details
 * page), and that bail-out must not swallow the entity update.
 */
const applyDealUpdated = (
    state: DealsState,
    payload: { pipelineId: number; dealId: number; updates: Partial<TDeal> }
): Partial<DealsState> => {
    const { pipelineId, dealId, updates } = payload;
    const deal = state.entities[dealId];
    if (!deal) return {};

    const fromPipelineId = pipelineId;
    const toPipelineId = updates.pipelineId ?? fromPipelineId;
    const fromStatusId = deal.status;
    const toStatusId = updates.status ?? fromStatusId;
    const needsColumnMove = fromPipelineId !== toPipelineId || fromStatusId !== toStatusId;

    const entities = { ...state.entities, [dealId]: { ...deal, ...updates } };
    if (!needsColumnMove) return { entities };

    const fromColumn = state.byPipeline[fromPipelineId]?.byStatus[fromStatusId];
    if (!fromColumn) return { entities };

    const toColumnBase =
        state.byPipeline[toPipelineId]?.byStatus[toStatusId] ??
        createEmptyStatusState(DEFAULT_PAGE_SIZE);

    const newFromColumn: DealsByStatusState = {
        ...fromColumn,
        ids: fromColumn.ids.filter((id) => id !== dealId),
        totalCount: Math.max(0, fromColumn.totalCount - 1),
    };
    const newToColumn: DealsByStatusState = {
        ...toColumnBase,
        ids: sortIdsByCreatedDesc([...toColumnBase.ids, dealId], entities),
        totalCount: toColumnBase.totalCount + 1,
    };

    const byPipeline = { ...state.byPipeline };
    byPipeline[fromPipelineId] = {
        byStatus: { ...byPipeline[fromPipelineId].byStatus, [fromStatusId]: newFromColumn },
    };
    byPipeline[toPipelineId] = {
        byStatus: { ...(byPipeline[toPipelineId]?.byStatus ?? {}), [toStatusId]: newToColumn },
    };

    return { entities, byPipeline };
};

/** Reverses an optimistic update that failed — mirrors `updateDealRollback`. */
const applyUpdateDealRollback = (
    state: DealsState,
    payload: { pipelineId: number; dealId: number; previousState: Partial<TDeal> }
): Partial<DealsState> => {
    const { dealId, previousState } = payload;
    const deal = state.entities[dealId];
    if (!deal) return {};

    const currentPipelineId = deal.pipelineId;
    const previousPipelineId = previousState.pipelineId ?? currentPipelineId;
    const currentStatusId = deal.status;
    const previousStatusId = previousState.status ?? currentStatusId;
    const needsColumnMove =
        currentPipelineId !== previousPipelineId || currentStatusId !== previousStatusId;

    const entities = { ...state.entities, [dealId]: { ...deal, ...previousState } };
    if (!needsColumnMove) return { entities };

    const fromColumn = state.byPipeline[currentPipelineId]?.byStatus[currentStatusId];
    if (!fromColumn) return {};

    const toColumn = state.byPipeline[previousPipelineId]?.byStatus[previousStatusId];
    if (!toColumn) return {};

    const newFromColumn: DealsByStatusState = {
        ...fromColumn,
        ids: fromColumn.ids.filter((id) => id !== dealId),
        totalCount: Math.max(0, fromColumn.totalCount - 1),
    };
    const newToColumn: DealsByStatusState = {
        ...toColumn,
        ids: sortIdsByCreatedDesc([...toColumn.ids, dealId], entities),
        totalCount: toColumn.totalCount + 1,
    };

    const byPipeline = { ...state.byPipeline };
    byPipeline[currentPipelineId] = {
        byStatus: { ...byPipeline[currentPipelineId].byStatus, [currentStatusId]: newFromColumn },
    };
    byPipeline[previousPipelineId] = {
        byStatus: { ...byPipeline[previousPipelineId].byStatus, [previousStatusId]: newToColumn },
    };

    return { entities, byPipeline };
};

/* ------------------------------------------------------------------ *
 * Store
 * ------------------------------------------------------------------ */

type DealsActions = {
    resetDealsState: () => void;
    initStatusColumn: (payload: { pipelineId: number; statusId: number; pageSize: number }) => void;
    fetchDealsByStatus: (args: { pipelineId: number; statusId: number }) => Promise<void>;
    createDeal: (payload: TCreateDealPayload) => Promise<TDeal>;
    updateDeal: (args: {
        dealId: number;
        pipelineId: number;
        updates: Partial<TDeal>;
    }) => Promise<void>;
    deleteDeal: (args: { dealId: number; pipelineId: number }) => Promise<number>;
    dealsEntitiesUpserted: (deals: TDeal[]) => void;
    applyDealsFilters: (filters: TDealsFilters) => void;
    resetDealsFilters: () => void;
};

export const useDealsStore = create<DealsState & DealsActions>()(
    devtools(
        (set, get) => ({
            ...initialDealsState,

            resetDealsState: () => set(initialDealsState, false, "resetDealsState"),

            initStatusColumn: ({ pipelineId, statusId }) =>
                set(
                    (state) => {
                        if (state.byPipeline[pipelineId]?.byStatus[statusId]) return {};
                        return {
                            byPipeline: withColumn(state, pipelineId, statusId, (c) => c, true),
                        };
                    },
                    false,
                    "initStatusColumn"
                ),

            fetchDealsByStatus: async ({ pipelineId, statusId }) => {
                const { filters } = get();

                const column = get().byPipeline[pipelineId]?.byStatus[statusId];
                if (!column) {
                    get().initStatusColumn({ pipelineId, statusId, pageSize: DEFAULT_PAGE_SIZE });
                }

                const current = get().byPipeline[pipelineId].byStatus[statusId];

                if (current.loading) return;
                if (current.totalPages > 0 && current.page >= current.totalPages) return;

                const nextPage = current.page + 1;

                try {
                    set(
                        (state) => ({
                            byPipeline: withColumn(state, pipelineId, statusId, (c) => ({
                                ...c,
                                loading: true,
                                error: undefined,
                            })),
                        }),
                        false,
                        "fetchDeals/pending"
                    );

                    const response = await dealsService.getAll({
                        pipelineId,
                        status: statusId,
                        page: nextPage,
                        pageSize: current.pageSize,

                        // global filters
                        ...filters,
                    });

                    const { data, meta } = response;
                    set(
                        (state) => {
                            const entities = { ...state.entities };
                            data.forEach((deal: TDeal) => {
                                entities[deal.id] = { ...(entities[deal.id] ?? {}), ...deal };
                            });

                            return {
                                entities,
                                byPipeline: withColumn(state, pipelineId, statusId, (c) => {
                                    // Add new deal IDs, avoiding duplicates
                                    const newIds = data
                                        .map((deal) => deal.id)
                                        .filter((id) => !c.ids.includes(id));
                                    return {
                                        ...c,
                                        ids: [...c.ids, ...newIds],
                                        page: meta.page,
                                        pageSize: meta.pageSize,
                                        totalPages: meta.totalPages,
                                        totalCount: meta.totalCount,
                                        loading: false,
                                        initialized: true,
                                    };
                                }),
                            };
                        },
                        false,
                        "fetchDeals/fulfilled"
                    );
                } catch (error: unknown) {
                    set(
                        (state) => ({
                            byPipeline: withColumn(state, pipelineId, statusId, (c) => ({
                                ...c,
                                loading: false,
                                error: getErrorFromBackend(error),
                            })),
                        }),
                        false,
                        "fetchDeals/rejected"
                    );
                    throw error;
                }
            },

            createDeal: async (payload) => {
                const deal = await dealsService.save(payload);
                set(
                    (state) => {
                        const entities = { ...state.entities, [deal.id]: deal };
                        const byPipeline = withColumn(
                            state,
                            deal.pipelineId,
                            deal.status,
                            (c) => {
                                // A brand-new column starts already initialized (the deal is its content).
                                const base = c.initialized
                                    ? c
                                    : { ...c, page: 1, totalPages: 1, initialized: true };
                                if (base.ids.includes(deal.id)) return base;
                                return {
                                    ...base,
                                    ids: [deal.id, ...base.ids],
                                    totalCount: base.totalCount + 1,
                                };
                            },
                            true
                        );
                        return { entities, byPipeline };
                    },
                    false,
                    "createDeal/fulfilled"
                );
                return deal;
            },

            updateDeal: async ({ dealId, pipelineId, updates }) => {
                const deal = get().entities[dealId];
                if (!deal) throw new Error("Deal not found");

                const previousState = Object.fromEntries(
                    Object.keys(updates).map((key) => [key, deal[key as keyof TDeal]])
                ) as Partial<TDeal>;

                // Optimistic move; a second pass lands the server's authoritative copy.
                set(
                    (state) => applyDealUpdated(state, { pipelineId, dealId, updates }),
                    false,
                    "updateDeal/optimistic"
                );

                try {
                    const updatedDeal = await dealsService.updateById(dealId, updates);
                    set(
                        (state) => applyDealUpdated(state, { pipelineId, dealId, updates: updatedDeal }),
                        false,
                        "updateDeal/fulfilled"
                    );
                } catch (err) {
                    set(
                        (state) =>
                            applyUpdateDealRollback(state, { pipelineId, dealId, previousState }),
                        false,
                        "updateDeal/rollback"
                    );
                    throw err;
                }
            },

            deleteDeal: async ({ dealId }) => {
                await dealsService.deleteDeal(dealId);
                set(
                    (state) => {
                        const deal = state.entities[dealId];
                        if (!deal) return {};

                        const entities = { ...state.entities };
                        delete entities[dealId];

                        const byPipeline =
                            withColumn(state, deal.pipelineId, deal.status, (c) => ({
                                ...c,
                                ids: c.ids.filter((id) => id !== dealId),
                                totalCount: Math.max(0, c.totalCount - 1),
                            })) ?? state.byPipeline;

                        return { entities, byPipeline };
                    },
                    false,
                    "deleteDeal/fulfilled"
                );
                return dealId;
            },

            dealsEntitiesUpserted: (deals) =>
                set(
                    (state) => {
                        const entities = { ...state.entities };
                        deals.forEach((deal) => {
                            entities[deal.id] = { ...(entities[deal.id] ?? {}), ...deal };
                        });
                        return { entities };
                    },
                    false,
                    "dealsEntitiesUpserted"
                ),

            applyDealsFilters: (newFilters) =>
                set(
                    (state) => {
                        let filters = { ...state.filters, ...newFilters };
                        // clear falsy values
                        filters = Object.fromEntries(
                            Object.entries(filters).filter(([, val]) => Boolean(val))
                        );

                        // set new filters and reset the dataset (single source of truth)
                        return { filters, entities: {}, byPipeline: {}, initialized: false };
                    },
                    false,
                    "applyDealsFilters"
                ),

            resetDealsFilters: () =>
                set(
                    { filters: {}, entities: {}, byPipeline: {}, initialized: false },
                    false,
                    "resetDealsFilters"
                ),
        }),
        { name: "deals" }
    )
);

/* ------------------------------------------------------------------ *
 * Selector hooks — replacements for the old createSelector factories.
 * ------------------------------------------------------------------ */

export const useDealsByPipelineAndStatus = (pipelineId: number, statusId: number): TDeal[] => {
    const ids = useDealsStore((s) => s.byPipeline[pipelineId]?.byStatus[statusId]?.ids);
    const entities = useDealsStore((s) => s.entities);
    return useMemo(
        () => (ids ?? []).map((id) => entities[id]).filter(Boolean) as TDeal[],
        [ids, entities]
    );
};

export const useStatusColumnMeta = (pipelineId: number, statusId: number) => {
    const column = useDealsStore((s) => s.byPipeline[pipelineId]?.byStatus[statusId]);
    return useMemo(
        () =>
            column && {
                page: column.page,
                pageSize: column.pageSize,
                totalPages: column.totalPages,
                totalCount: column.totalCount,
                loading: column.loading,
                initialized: column.initialized,
                hasMore: column.page < column.totalPages,
                error: column.error,
            },
        [column]
    );
};
